import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Alert, Loader } from '@lms/ui';
import { AssessmentAttemptReview } from '@/components/assessments/AssessmentAttemptReview';
import { AttemptConnectionBanner } from '@/components/assessments/AttemptConnectionBanner';
import { AssessmentConfirmPanel } from '@/components/assessments/AssessmentConfirmPanel';
import { QuizPlayer, type QuizQuestion } from '@/components/assessments/QuizPlayer';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { useAttemptSync } from '@/hooks/useAttemptSync';
import { api } from '@/lib/api';
import { clockOffsetMs, readClockOffset } from '@/lib/attempt-clock';
import { AttemptStaffFlags } from '@/components/assessments/AttemptStaffFlags';
import { browserAttemptStore } from '@/lib/attempt-offline-store';
import { useAuthStore } from '@/stores/authStore';
import { quizAttemptBack } from '@/lib/assessment-navigation';
import { showsScoreAndPercent } from '@/lib/assessment-score-display';

interface AttemptSettings {
  type?: string;
  time_limit_minutes?: number;
  require_passing_score?: boolean;
  show_score_and_percent?: boolean;
}

interface PreludePayload {
  assessment: {
    id?: string;
    title: string;
    code?: string;
    type?: string;
    currentVersion?: { settings?: AttemptSettings | null };
  };
  attemptLimits?: {
    maxAttempts: number | null;
    attemptsUsed: number;
    canStartNewAttempt: boolean;
    pendingReview?: boolean;
  };
  timeLimitSeconds?: number | null;
  activeAttempt?: {
    id: string;
    number: number;
    startedAt: string;
    status: string;
  } | null;
  previousAttempts: {
    id: string;
    number: number;
    date: string;
    status: string;
    score: number | null;
    maxScore: number;
    result: 'pass' | 'fail' | 'in_progress' | null;
  }[];
}

interface AttemptPayload {
  attempt: {
    id: string;
    number?: number;
    status?: string;
    startedAt?: string;
    deadlineAt?: string | null;
    submissionSource?: string | null;
    late?: boolean;
  };
  serverNow?: string;
  graceUntil?: string | null;
  assessment: {
    id?: string;
    title: string;
    code?: string;
    currentVersion?: { settings?: AttemptSettings | null };
  };
  timeLimitSeconds?: number | null;
  remainingSeconds?: number | null;
  responses?: { questionId: string; response: unknown }[];
  questions: {
    id: string;
    stem: string;
    type?: string;
    currentVersion?: { options?: { id: string; label?: string; content: string }[] };
  }[];
}

const TYPE_LABELS: Record<string, string> = {
  exam: 'Examen',
  quiz: 'Quiz',
  survey: 'Encuesta',
};

const TYPE_PREFIX: Record<string, string> = {
  exam: 'Examen: ',
  quiz: 'Quiz: ',
  survey: 'Encuesta: ',
};

const TYPE_ICONS: Record<string, string> = {
  exam: 'assignment',
  quiz: 'quiz',
  survey: 'poll',
};

interface AssessmentPlayerProps {
  courseId: string;
  sectionItemId: string;
  assessmentInstanceId: string;
  isAdminPreview?: boolean;
  backTo: string;
  backLabel?: string;
  itemTitle?: string;
}

export function AssessmentPlayer({
  courseId,
  sectionItemId,
  assessmentInstanceId,
  isAdminPreview = false,
  backTo,
  backLabel = 'Volver al curso',
  itemTitle,
}: AssessmentPlayerProps) {
  const queryClient = useQueryClient();
  const userId = useAuthStore((state) => state.user?.id);
  const { hasPermission } = usePermissions();
  const canManageAttempts = hasPermission(PERMISSIONS.EVALUACIONES_INTENTOS_GESTIONAR);
  const canAttempt = !isAdminPreview || hasPermission(PERMISSIONS.EVALUACIONES_INTENTAR);
  const [started, setStarted] = useState(false);
  const [resumedAttemptId, setResumedAttemptId] = useState<string | null>(null);
  const [reviewAttemptId, setReviewAttemptId] = useState<string | null>(null);
  const [reviewEditMode, setReviewEditMode] = useState(false);
  const [deletingAttemptId, setDeletingAttemptId] = useState<string | null>(null);

  const preludeQueryKey = ['assessment-prelude', isAdminPreview ? 'admin' : 'student', courseId, sectionItemId] as const;

  const deleteAttemptMutation = useMutation({
    mutationFn: (attemptId: string) =>
      api(`/assessments/attempts/${attemptId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeletingAttemptId(null);
      void queryClient.invalidateQueries({ queryKey: preludeQueryKey });
      void queryClient.invalidateQueries({ queryKey: ['student', 'courses', courseId] });
    },
    onError: () => {
      setDeletingAttemptId(null);
    },
  });

  const handleDeleteAttempt = (attemptId: string) => {
    if (!window.confirm('¿Eliminar este intento? Esta acción no se puede deshacer.')) return;
    setDeletingAttemptId(attemptId);
    deleteAttemptMutation.mutate(attemptId);
  };

  const preludeQuery = useQuery({
    queryKey: preludeQueryKey,
    queryFn: () => {
      if (isAdminPreview) {
        return api<PreludePayload>(`/assessments/instances/${assessmentInstanceId}/prelude`);
      }
      return api<PreludePayload>(
        `/student/courses/${courseId}/items/${sectionItemId}/assessment/prelude`,
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    placeholderData: (previous) => previous,
  });

  const activeAttemptId = preludeQuery.data?.activeAttempt?.id;

  useEffect(() => {
    if (activeAttemptId && !started && !resumedAttemptId) {
      setResumedAttemptId(activeAttemptId);
      setStarted(true);
    }
  }, [activeAttemptId, started, resumedAttemptId]);

  const startMutation = useMutation({
    mutationFn: () => {
      if (isAdminPreview) {
        return api<{ id: string }>(`/assessments/attempts/${assessmentInstanceId}/start`, { method: 'POST' });
      }
      return api<{ id: string }>(
        `/student/courses/${courseId}/items/${sectionItemId}/assessment/start`,
        { method: 'POST' },
      );
    },
    onSuccess: (attempt) => {
      setResumedAttemptId(attempt.id);
      setStarted(true);
    },
  });

  const attemptId = resumedAttemptId ?? startMutation.data?.id;
  const sync = useAttemptSync({
    userId,
    attemptId,
    progressUrl: attemptId
      ? isAdminPreview
        ? `/assessments/attempts/${attemptId}/progress`
        : `/student/assessment-attempts/${attemptId}/progress`
      : null,
    submitUrl: attemptId
      ? isAdminPreview
        ? `/assessments/attempts/${attemptId}/submit`
        : `/student/assessment-attempts/${attemptId}/submit`
      : null,
    fileUrl: attemptId
      ? isAdminPreview
        ? `/assessments/attempts/${attemptId}/questions`
        : `/student/assessment-attempts/${attemptId}/questions`
      : null,
    onSynced: () => {
      void queryClient.invalidateQueries({
        queryKey: ['assessment-attempt', isAdminPreview ? 'admin' : 'student', attemptId],
      });
      void queryClient.invalidateQueries({ queryKey: ['course-play'] });
    },
  });

  const attemptQuery = useQuery({
    queryKey: ['assessment-attempt', isAdminPreview ? 'admin' : 'student', attemptId],
    queryFn: async () => {
      await sync.flush();
      if (isAdminPreview) {
        return api<AttemptPayload>(`/assessments/attempts/${attemptId}`);
      }
      return api<AttemptPayload>(`/student/assessment-attempts/${attemptId}`);
    },
    enabled: Boolean(attemptId) && started,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    placeholderData: (previous) => previous,
  });

  useEffect(() => {
    if (!sync.accepted || !attemptId) return;
    setReviewAttemptId(attemptId);
    setStarted(false);
    void queryClient.invalidateQueries({
      queryKey: ['assessment-prelude', isAdminPreview ? 'admin' : 'student', courseId, sectionItemId],
    });
    void queryClient.invalidateQueries({ queryKey: ['student', 'courses', courseId] });
  }, [sync.accepted, attemptId, queryClient, isAdminPreview, courseId, sectionItemId]);

  const prelude = preludeQuery.data;
  const preludeSettings = prelude?.assessment?.currentVersion?.settings ?? {};
  const preludeType = prelude?.assessment?.type ?? preludeSettings.type ?? 'exam';
  const titlePrefix = TYPE_PREFIX[preludeType] ?? '';
  const confirmTitle = `${titlePrefix}${prelude?.assessment?.title ?? itemTitle ?? 'Evaluación'}`;

  const attemptData = attemptQuery.data;
  const attemptSettings = attemptData?.assessment?.currentVersion?.settings ?? {};
  const assessmentType = attemptSettings.type ?? preludeType;
  const typeLabel = TYPE_LABELS[assessmentType] ?? 'Examen';
  const quizTitlePrefix = TYPE_PREFIX[assessmentType] ?? '';
  const typeIcon = TYPE_ICONS[assessmentType] ?? 'assignment';
  const closedStatus = attemptData?.attempt?.status;
  const isSubmitted =
    closedStatus === 'submitted' ||
    closedStatus === 'pending_review' ||
    closedStatus === 'approved' ||
    closedStatus === 'rejected' ||
    closedStatus === 'expired_empty' ||
    sync.accepted;
  const loadedAssessmentId = attemptData?.assessment?.id ?? prelude?.assessment?.id;
  const assessmentBack =
    isAdminPreview && loadedAssessmentId
      ? quizAttemptBack(new URLSearchParams(), loadedAssessmentId)
      : null;
  const resolvedBackTo = assessmentBack?.to ?? backTo;
  const resolvedBackLabel = assessmentBack?.label ?? backLabel;

  const questions: QuizQuestion[] =
    attemptData?.questions.map((q, index) => ({
      id: q.id,
      index,
      stem: q.stem,
      type: q.type,
      options: q.currentVersion?.options?.map((opt) => ({
        id: opt.id,
        label: opt.label ?? '',
        content: opt.content,
      })),
    })) ?? [];

  const responseKey =
    attemptData?.responses
      ?.map((row) => `${row.questionId}:${JSON.stringify(row.response)}`)
      .join('|') ?? '';
  const initialAnswers = useMemo(() => {
    const map: Record<string, unknown> = {};
    attemptData?.responses?.forEach((row) => {
      map[row.questionId] = row.response;
    });
    if (userId && attemptId) {
      const local = browserAttemptStore().load(userId, attemptId);
      if (local) Object.assign(map, local.answers);
    }
    return map;
  }, [responseKey, userId, attemptId, attemptData?.responses]);

  useEffect(() => {
    if (!attemptData?.attempt?.deadlineAt && !attemptData?.graceUntil) return;
    sync.rememberTiming(attemptData?.attempt?.deadlineAt ?? null, attemptData?.graceUntil ?? null);
  }, [attemptData?.attempt?.deadlineAt, attemptData?.graceUntil, sync.rememberTiming]);

  useEffect(() => {
    sync.noteServerNow(attemptData?.serverNow);
  }, [attemptData?.serverNow, sync.noteServerNow]);

  if (preludeQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader label="Cargando evaluación…" />
      </div>
    );
  }

  if (preludeQuery.error && !preludeQuery.data) {
    return (
      <Alert variant="danger" className="m-4">
        {(preludeQuery.error as Error).message}
      </Alert>
    );
  }

  if (reviewAttemptId) {
    const reviewAttemptNumber =
      prelude?.previousAttempts.find((attempt) => attempt.id === reviewAttemptId)?.number ??
      (prelude?.activeAttempt?.id === reviewAttemptId ? prelude.activeAttempt.number : undefined) ??
      (prelude?.previousAttempts.length ?? 0) + 1;

    return (
      <AssessmentAttemptReview
        attemptId={reviewAttemptId}
        isAdminPreview={isAdminPreview}
        backTo={resolvedBackTo}
        backLabel={resolvedBackLabel}
        attemptNumber={reviewAttemptNumber}
        titlePrefix={titlePrefix}
        canManageAttempts={canManageAttempts}
        initialEditMode={reviewEditMode}
        onBack={() => {
          setReviewAttemptId(null);
          setReviewEditMode(false);
          void queryClient.invalidateQueries({ queryKey: preludeQueryKey });
        }}
      />
    );
  }

  if (!started) {
    return (
      <>
        {startMutation.error && (
          <Alert variant="danger" className="mx-4 mb-4">
            {(startMutation.error as Error).message}
          </Alert>
        )}
        {deleteAttemptMutation.error && (
          <Alert variant="danger" className="mx-4 mb-4">
            {(deleteAttemptMutation.error as Error).message}
          </Alert>
        )}
        <AssessmentConfirmPanel
          title={confirmTitle}
          previousAttempts={prelude?.previousAttempts ?? []}
          showGrades={showsScoreAndPercent(prelude?.assessment.currentVersion?.settings)}
          onStart={() => startMutation.mutate()}
          onViewAttempt={(id) => {
            setReviewEditMode(false);
            setReviewAttemptId(id);
          }}
          onEditAttempt={(id) => {
            setReviewEditMode(true);
            setReviewAttemptId(id);
          }}
          onDeleteAttempt={canManageAttempts ? handleDeleteAttempt : undefined}
          canManageAttempts={canManageAttempts}
          deletingAttemptId={deletingAttemptId}
          starting={startMutation.isPending}
          backTo={resolvedBackTo}
          backLabel={resolvedBackLabel}
          hasActiveAttempt={Boolean(prelude?.activeAttempt)}
          canAttempt={canAttempt}
          canStartNewAttempt={prelude?.attemptLimits?.canStartNewAttempt ?? true}
          pendingReview={prelude?.attemptLimits?.pendingReview}
          timeLimitSeconds={prelude?.timeLimitSeconds}
          maxAttempts={prelude?.attemptLimits?.maxAttempts ?? null}
          attemptsUsed={
            prelude?.attemptLimits?.attemptsUsed ??
            (prelude?.previousAttempts.length ?? 0) + (prelude?.activeAttempt ? 1 : 0)
          }
        />
      </>
    );
  }

  const startError = startMutation.error as Error | null;
  const loadError = attemptQuery.error as Error | null;
  const attemptNumber =
    prelude?.activeAttempt?.number ??
    attemptData?.attempt?.number ??
    (prelude?.previousAttempts.length ?? 0) + 1;

  return (
    <div className="lms-quiz-attempt font-sans">
      <AttemptConnectionBanner offline={!sync.online} />
      <div className="lms-site-admin-hub-container py-8 md:py-12">
        <header className="lms-quiz-attempt__header">
          <HubBackLink to={resolvedBackTo} label={resolvedBackLabel} />
          <div className="lms-quiz-attempt__header-copy">
            <h1>
              {quizTitlePrefix}
              {attemptData?.assessment?.title ?? prelude?.assessment?.title ?? itemTitle ?? 'Evaluación'}
            </h1>
            {attemptData && (
              <p>
                <span className="material-symbols-outlined" aria-hidden="true">
                  {typeIcon}
                </span>
                Intento {attemptNumber} · {typeLabel} ·{' '}
                {attemptData.assessment?.code ?? prelude?.assessment?.code ?? '—'}
              </p>
            )}
            {isSubmitted && (
              <AttemptStaffFlags
                late={attemptData?.attempt?.late}
                submissionSource={attemptData?.attempt?.submissionSource}
              />
            )}
          </div>
        </header>

        {startError && !sync.queued && (
          <Alert variant="danger" className="mb-4">
            {startError.message}
          </Alert>
        )}
        {loadError && !attemptData && !sync.queued && (
          <Alert variant="danger" className="mb-4">
            {loadError.message}
          </Alert>
        )}
        {attemptQuery.isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <QuizPlayer
            questions={questions}
            timeLimitSeconds={attemptData?.timeLimitSeconds}
            remainingSeconds={attemptData?.remainingSeconds}
            startedAt={attemptData?.attempt?.startedAt}
            deadlineAt={attemptData?.attempt?.deadlineAt ?? sync.deadlineAt}
            clockOffsetMs={sync.serverOffsetMs ?? (attemptData?.serverNow ? clockOffsetMs(attemptData.serverNow) : readClockOffset())}
            saveStatus={sync.saveStatus}
            initialAnswers={initialAnswers}
            onLocalChange={(answers) => sync.persistAnswers(answers)}
            onSaveProgress={(answers) => sync.persistAnswers(answers)}
            onUploadFile={(questionId, file) => sync.uploadFile(questionId, file)}
            onSubmit={(answers) => {
              void sync.submit(answers);
            }}
            submitting={sync.sending}
            submitted={isSubmitted}
            queued={sync.queued && !isSubmitted}
            warnBeforeUnload={!isSubmitted && (sync.dirty || sync.queued)}
          />
        )}
      </div>
    </div>
  );
}
